/**
 * EvidenceSprintPanel — the Sprint 0 / Evidence Gate cockpit on an assessment run.
 *
 * The kickoff gate between the EKSELENS360 diagnostic and the 90-day mobilise:
 * an evidence-confidence dial, the binding-constraint focus, a living evidence
 * register, owner-editable RACI + rate-card-guided cost, the 5 binary acceptance
 * criteria, and the binary go/hold gate (mobilise stays blocked until every
 * criterion is met). One-click editable Word charter.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Rocket, Download, ShieldCheck, AlertTriangle, CheckCircle2, RefreshCw, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const BASE = process.env.REACT_APP_BACKEND_URL;
const RAG = { green: "text-emerald-700 bg-emerald-50 border-emerald-200", amber: "text-amber-700 bg-amber-50 border-amber-200", red: "text-red-700 bg-red-50 border-red-200" };
const STATUSES = ["Outstanding", "Received", "Validated", "Accepted"];
const ST_CLS = { Outstanding: "bg-red-50 text-red-700", Received: "bg-amber-50 text-amber-700", Validated: "bg-emerald-50 text-emerald-700", Accepted: "bg-emerald-100 text-emerald-800" };

export default function EvidenceSprintPanel({ runId }) {
  const [model, setModel] = useState(null);
  const [meta, setMeta] = useState({ initialised: false, all_criteria_met: false, register_summary: {} });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [memo, setMemo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/evidence-sprint`);
      setModel(data.model);
      setMeta({ initialised: data.initialised, all_criteria_met: data.all_criteria_met, register_summary: data.register_summary });
    } catch (e) {
      if (e?.response?.status !== 404) toast.error(e?.response?.data?.detail || "Failed to load Evidence Sprint");
    } finally { setLoading(false); }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (patch, ok = "Saved") => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/assessments/runs/${runId}/evidence-sprint`, patch);
      setModel(data.model);
      setMeta((m) => ({ ...m, all_criteria_met: data.all_criteria_met, register_summary: data.register_summary }));
      toast.success(ok);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  }, [runId]);

  const init = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/assessments/runs/${runId}/evidence-sprint`); setModel(data.model); setMeta((m) => ({ ...m, initialised: true })); toast.success("Sprint initialised"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Init failed"); }
    finally { setBusy(false); }
  }, [runId, load]);

  const decide = useCallback(async (decision) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/assessments/runs/${runId}/evidence-sprint/gate-decision`, { decision, memo });
      setModel(data.model);
      toast.success(decision === "go" ? "Mobilise released ✓" : "Held — mobilise not released");
    } catch (e) { toast.error(e?.response?.data?.detail || "Gate decision failed"); }
    finally { setBusy(false); }
  }, [runId, memo]);

  if (loading && !model) return <div className="bg-white border border-zinc-200 p-5 text-zinc-500 text-[13px]">Loading Evidence Sprint…</div>;
  if (!model) return null;

  const bc = model.binding_constraint || {};
  const cost = model.cost || {};
  const sym = { GBP: "£", USD: "$", EUR: "€" }[cost.currency] || "";
  const updateCrit = (id, met) => save({ acceptance_criteria: model.acceptance_criteria.map((c) => c.id === id ? { ...c, met } : c) }, "Criterion updated");
  const updateReg = (rid, field, val) => save({ register: model.register.map((r) => r.id === rid ? { ...r, [field]: val } : r) }, "Register updated");

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="evidence-sprint-panel">
      <div className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em] mb-3">
        <Rocket size={14} /> Sprint 0 · Evidence Gate
      </div>

      {/* Confidence dial */}
      <div className={`border rounded p-3 flex items-center justify-between ${RAG[model.rag] || RAG.red}`} data-testid="evidence-sprint-dial">
        <div>
          <div className="text-[10.5px] uppercase tracking-wider opacity-80">Evidence confidence</div>
          <div className="text-[26px] font-semibold leading-none mt-1">{model.current_pct}%<span className="text-[13px] font-normal opacity-70"> → ≥{model.target_pct}% target</span></div>
        </div>
        <div className="text-right text-[11px]">
          <div className="font-semibold uppercase">{model.rag}</div>
          <div className="opacity-80">{model.dim_count} dims · {model.indicator_count} indicators</div>
        </div>
      </div>

      {/* Mobilise gate status */}
      <div className={`mt-3 flex items-center gap-2 text-[12px] px-3 py-2 border rounded ${model.mobilise_blocked ? "bg-red-50 border-red-200 text-red-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`} data-testid="evidence-sprint-mobilise-status">
        {model.mobilise_blocked ? <Lock size={13} /> : <Unlock size={13} />}
        {model.mobilise_blocked ? "Mobilise budget BLOCKED until the exit gate is passed (all criteria met → go)." : `Mobilise RELEASED — gate decision: ${model.gate?.decision} by ${model.gate?.decided_by || "—"}.`}
      </div>

      {/* Binding constraint */}
      {bc.name && (
        <div className="mt-3 border-l-2 border-[#7737F6] pl-3" data-testid="evidence-sprint-binding">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Binding constraint (validate first)</div>
          <div className="text-[13.5px] text-black font-medium">{bc.name} · {bc.score}/{bc.scale_max} <span className="text-zinc-400 font-normal">{bc.level_label}</span></div>
        </div>
      )}

      <a href={`${BASE}/api/admin/assessments/runs/${runId}/evidence-sprint/charter.docx`}
        className="mt-4 inline-flex items-center gap-2 bg-[#0B0B0C] text-white px-4 py-2 text-[12px] tracking-[0.14em] uppercase rounded hover:bg-black/80 transition-colors" data-testid="evidence-sprint-charter-docx">
        <Download size={14} /> Editable charter (Word)
      </a>

      {!meta.initialised ? (
        <button type="button" onClick={init} disabled={busy} className="mt-4 w-full bg-[#7737F6] hover:bg-[#5D28C7] disabled:opacity-60 text-white text-[13px] font-medium px-3 py-2 flex items-center justify-center gap-2" data-testid="evidence-sprint-init">
          {busy ? <RefreshCw size={14} className="animate-spin" /> : <Rocket size={14} />} Initialise sprint (seed register & gate)
        </button>
      ) : (
        <div className="mt-5 space-y-5">
          {/* Evidence register */}
          <div data-testid="evidence-sprint-register">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500">Evidence register</div>
              <div className="text-[11px] text-zinc-500">{meta.register_summary.closed_pct ?? 0}% closed</div>
            </div>
            <div className="space-y-1.5">
              {(model.register || []).map((r) => (
                <div key={r.id} className="border border-zinc-200 px-3 py-2 text-[12px]" data-testid={`evidence-sprint-reg-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-zinc-800">{r.artefact_required}</span>
                    <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 ${r.priority === "HIGH" ? "text-red-700 bg-red-50" : "text-amber-700 bg-amber-50"}`}>{r.priority}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <input value={r.source_owner} onChange={(e) => setModel((m) => ({ ...m, register: m.register.map((x) => x.id === r.id ? { ...x, source_owner: e.target.value } : x) }))}
                      onBlur={(e) => updateReg(r.id, "source_owner", e.target.value)} placeholder="Source / owner"
                      className="border border-zinc-300 px-2 py-1 text-[11px] flex-1" data-testid={`evidence-sprint-reg-owner-${r.id}`} />
                    <select value={r.status} onChange={(e) => updateReg(r.id, "status", e.target.value)}
                      className={`text-[11px] px-2 py-1 border border-zinc-300 ${ST_CLS[r.status] || ""}`} data-testid={`evidence-sprint-reg-status-${r.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cost (rate-card guide, editable) */}
          <div data-testid="evidence-sprint-cost">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">Cost — rate-card guide (editable)</div>
            {(cost.lines || []).map((l, i) => (
              <div key={i} className="flex items-center gap-2 mb-1.5 text-[12px]">
                <span className="flex-1 text-zinc-700">{l.line}</span>
                <input type="number" value={l.days} onChange={(e) => setModel((m) => { const c = { ...m.cost, lines: m.cost.lines.map((x, j) => j === i ? { ...x, days: +e.target.value } : x) }; return { ...m, cost: c }; })}
                  className="w-14 border border-zinc-300 px-2 py-1 text-[11px]" data-testid={`evidence-sprint-cost-days-${i}`} /> <span className="text-zinc-400 text-[10px]">days ×</span>
                <input type="number" value={l.day_rate} onChange={(e) => setModel((m) => { const c = { ...m.cost, lines: m.cost.lines.map((x, j) => j === i ? { ...x, day_rate: +e.target.value } : x) }; return { ...m, cost: c }; })}
                  className="w-20 border border-zinc-300 px-2 py-1 text-[11px]" data-testid={`evidence-sprint-cost-rate-${i}`} />
              </div>
            ))}
            <div className="flex items-center justify-between mt-2">
              <span className="text-[13px] font-semibold text-black">Total fixed price: {sym}{(cost.total || 0).toLocaleString()}</span>
              <button type="button" disabled={busy} onClick={() => save({ cost: model.cost }, "Cost saved")} className="text-[11px] text-[#7737F6] hover:underline disabled:opacity-50" data-testid="evidence-sprint-cost-save">Save cost</button>
            </div>
          </div>

          {/* Acceptance criteria */}
          <div data-testid="evidence-sprint-criteria">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">Acceptance criteria (exit gate)</div>
            {(model.acceptance_criteria || []).map((c) => (
              <label key={c.id} className="flex items-start gap-2 mb-1.5 text-[12px] cursor-pointer">
                <input type="checkbox" checked={!!c.met} onChange={(e) => updateCrit(c.id, e.target.checked)} className="mt-0.5" data-testid={`evidence-sprint-crit-${c.id}`} />
                <span className={c.met ? "text-emerald-700" : "text-zinc-700"}>{c.test}</span>
              </label>
            ))}
          </div>

          {/* Gate decision */}
          <div className="border-t border-zinc-200 pt-4" data-testid="evidence-sprint-gate">
            <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">Exit gate decision</div>
            <textarea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Exit memo / rationale…" rows={2}
              className="w-full border border-zinc-300 px-2 py-1.5 text-[12px] mb-2" data-testid="evidence-sprint-memo" />
            <div className="flex gap-2">
              <button type="button" disabled={busy || !meta.all_criteria_met} title={meta.all_criteria_met ? "" : "All acceptance criteria must be met"}
                onClick={() => decide("go")} className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-[12px] font-medium px-3 py-2 flex items-center justify-center gap-1.5" data-testid="evidence-sprint-go">
                <CheckCircle2 size={13} /> Go — release mobilise
              </button>
              <button type="button" disabled={busy} onClick={() => decide("hold")} className="flex-1 bg-zinc-700 hover:bg-zinc-800 disabled:opacity-50 text-white text-[12px] font-medium px-3 py-2 flex items-center justify-center gap-1.5" data-testid="evidence-sprint-hold">
                <AlertTriangle size={13} /> Hold
              </button>
            </div>
            {!meta.all_criteria_met && <p className="text-[11px] text-zinc-500 mt-2"><ShieldCheck size={11} className="inline" /> Mandatory gate — "Go" unlocks only when all five criteria are met.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
